import * as React from 'react';
import { useEffect, useCallback, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Checkbox, FormLabelWrapper, HelperTextRElement } from '@restworld/ui-ds-base';

import { Variable } from '@restworld/utility-types';

import { WizardStepProps } from '../../worker-application-wizard.types';
import { useWorkerApplicationCxt } from '../../worker-application-wizard.context';
import { WizardSelectField } from '../wizard-select-field';
import WizardMultiSelect from '../wizard-multi-select';

export const WizardFourthStep: React.FunctionComponent<WizardStepProps> = (props) => {
  const {
    setDisplacementAvailability,
    selectedDisplacementAvailabilityValue,
    fieldOptions: { drivingLicenseOptions, transportationOptions, displacementAvailability, displacementPreference },
  } = useWorkerApplicationCxt();
  return (
    <div className="flex flex-col gap-4">
      <DrivingLicense options={drivingLicenseOptions} />
      <UserAvailableOptions options={transportationOptions} />
      <DisplacementAvailability
        options={displacementAvailability}
        setDisplacementAvailability={setDisplacementAvailability}
      />
      <DisplacementPreference
        options={displacementPreference}
        selectedDisplacementAvailabilityValue={
          displacementAvailability.find((v) => v.id === selectedDisplacementAvailabilityValue)?.key
        }
      />
    </div>
  );
};

export default WizardFourthStep;
const DrivingLicense = ({ options }: OptionType) => {
  const { errorsTextMap } = useWorkerApplicationCxt();
  const id = 'driving_licenses';
  const { t } = useTranslation();
  const { control } = useFormContext();
  return (
    <FormLabelWrapper id={id} label={t('onboarding-page:what_licenses_do_you_have')}>
      <Controller
        key={id}
        name={id}
        control={control}
        render={({ field }) => (
          <ul className="flex flex-row gap-4 flex-wrap">
            {options?.map(({ key, label, id }) => {
              const selectedLicenseKeys = field.value?.reduce((keys: string[], id: string) => {
                const key = options?.find((o) => o.id === id).key;
                keys.push(key);
                return keys;
              }, []);
              const onChange = (e: { stopPropagation: () => void; target: { id: string; checked: boolean } }) => {
                e.stopPropagation();
                let checkedValues: string[] = Array.isArray(field.value) && field.value ? [...field.value] : [];
                checkedValues =
                  !checkedValues.includes(e.target.id) && e.target.checked
                    ? [...checkedValues, e.target.id]
                    : checkedValues.filter((v) => v !== e.target.id);
                field.onChange(checkedValues.length ? checkedValues : []);
              };

              const disabled =
                selectedLicenseKeys?.length &&
                ((selectedLicenseKeys?.includes('driving_license_none') && key !== 'driving_license_none') ||
                  (!selectedLicenseKeys?.includes('driving_license_none') && key === 'driving_license_none'));

              return (
                <li key={key}>
                  <Checkbox
                    {...field}
                    key={key}
                    testId={`${key}-checkbox`}
                    title={label}
                    id={id}
                    disabled={disabled}
                    onChange={onChange}
                    isChecked={field.value?.includes(id)}
                  />
                </li>
              );
            })}
          </ul>
        )}
      />
      <HelperTextRElement helperText={errorsTextMap[id] as string} />
    </FormLabelWrapper>
  );
};
const UserAvailableOptions = ({ options }: OptionType) => {
  const { t } = useTranslation();
  return (
    <WizardMultiSelect
      options={options}
      mainTitle={t('onboarding-page:select_one_or_more_options')}
      rules={{ required: true }}
      id="transportation_options"
      formLabel={t('onboarding-page:what_means_of_transportation_do_you_use_to_get_to_work')}
      testId="worker-transport-options"
    />
  );
};
interface DisplacementAvailabilityProps extends OptionType {
  setDisplacementAvailability: React.Dispatch<React.SetStateAction<string>>;
}
const DisplacementAvailability = ({ options, setDisplacementAvailability }: DisplacementAvailabilityProps) => {
  const { t } = useTranslation();
  const onChange = useCallback(
    (value: string) => {
      setDisplacementAvailability(value);
    },
    [setDisplacementAvailability]
  );
  const optionsCopy = useMemo(() => [...options].filter((v) => v.key !== 'no'), [options]);
  return (
    <WizardSelectField
      placeholder={t('common:select_an_answer')}
      id={'displacement_availability'}
      formLabel={t('onboarding-page:are_you_available_to_move')}
      options={optionsCopy}
      rules={{ required: true }}
      onChange={onChange}
      testId="displacement-availability-options"
      inputTestId="displacement-availability-input"
    />
  );
};
interface DisplacementPreferenceType extends OptionType {
  selectedDisplacementAvailabilityValue?: string;
}
const DisplacementPreference = ({ options, selectedDisplacementAvailabilityValue }: DisplacementPreferenceType) => {
  const { setValue } = useFormContext();
  const { t } = useTranslation();

  const negativeValue = 'displacement_availability_no'; // --> id for no selected
  const isDisabledState = useMemo(
    () => selectedDisplacementAvailabilityValue === negativeValue,
    [selectedDisplacementAvailabilityValue]
  );
  useEffect(() => {
    if (isDisabledState) setValue('displacement_preferences', undefined);
  }, [isDisabledState, setValue]);
  return (
    <WizardMultiSelect
      options={options}
      id="displacement_preferences"
      disabled={isDisabledState || !selectedDisplacementAvailabilityValue}
      formLabel={t('common:if_yes_where')}
      mainTitle={t('onboarding-page:where_would_you_like_to_move')}
      rules={{ required: !isDisabledState }}
      placeholder={t('common:select_an_answer')}
      testId="worker-displacement-preferences"
    />
  );
};
type OptionType = {
  options: Variable[];
};
// displacement_preferences: { displacement_preference_var: Variable }[]
